/**
 * Megaproject form grammar (D-1121 card B): `(kind, maker, seed, site, stage)` → a part list of primitives.
 * Pure and deterministic; view-side only (no sim effect). Each kind × maker is a form family with ≥ 6 silhouette slots
 * of 3–4 options (≥ 2 read off the site), continuous proportions, and a 5-step build order.
 */
import { createRng, nextFloat } from '../../core/rng.ts'
import { longHoldHoldfast, longHoldMutual, listeningSailsHoldfast, commonsMutual } from './holdfastMutual.ts'
import { listeningSailsAuthority, spanAuthority, trunkLineMeridian, spanMeridian } from './authorityMeridian.ts'
import { oreSlingTethys, crownForkTethys, provingRangeVarro, colossusSlipVarro } from './tethysVarro.ts'
import { hashSeed } from './kit.ts'
import { MEGA_STAGES, PROP_COUNT, type FamilyGrammar, type FamilyId, type FormGenome, type MegaKind, type MegaMaker, type MegaPart, type SiteContext } from './types.ts'

export * from './types.ts'

const GRAMMARS: readonly FamilyGrammar[] = [
  longHoldHoldfast,
  longHoldMutual,
  listeningSailsHoldfast,
  listeningSailsAuthority,
  trunkLineMeridian,
  oreSlingTethys,
  provingRangeVarro,
  spanMeridian,
  spanAuthority,
  commonsMutual,
  colossusSlipVarro,
  crownForkTethys,
]

export function familyId(kind: MegaKind, maker: MegaMaker): FamilyId {
  return `${kind}/${maker}`
}

/** Every form family, in catalogue order. */
export const FAMILIES: readonly FamilyId[] = GRAMMARS.map((f) => familyId(f.kind, f.maker))

const BY_ID: ReadonlyMap<FamilyId, FamilyGrammar> = new Map(GRAMMARS.map((f) => [familyId(f.kind, f.maker), f]))

/** The grammar for a kind × maker, or undefined when that maker never builds that kind. */
export function familyGrammar(kind: MegaKind, maker: MegaMaker): FamilyGrammar | undefined {
  return BY_ID.get(familyId(kind, maker))
}

function requireGrammar(kind: MegaKind, maker: MegaMaker): FamilyGrammar {
  const f = familyGrammar(kind, maker)
  if (f === undefined) throw new Error(`megaproject grammar: ${maker} does not build ${kind}`)
  return f
}

const FAMILY_SALT: Record<MegaKind, number> = {
  longHold: 1,
  listeningSails: 2,
  trunkLine: 3,
  oreSling: 4,
  provingRange: 5,
  span: 6,
  commons: 7,
  colossusSlip: 8,
  crownFork: 9,
}

/** The form genome: silhouette slots (seeded, or read off the site) plus continuous proportions. */
export function formGenome(kind: MegaKind, maker: MegaMaker, seed: number, site: SiteContext): FormGenome {
  const f = requireGrammar(kind, maker)
  const makerSalt = (['holdfast', 'mutual', 'authority', 'meridian', 'tethys', 'varro_security'] as const).indexOf(maker) + 1
  const rng = createRng(hashSeed(seed >>> 0, FAMILY_SALT[kind], makerSalt, 0x666f726d))
  const slots = f.slots.map((s) => {
    const drawn = Math.floor(nextFloat(rng) * s.options) // always drawn, so site slots never shift the others
    return s.site === undefined ? drawn : Math.min(s.options - 1, Math.max(0, s.site(site)))
  })
  const props: number[] = []
  for (let i = 0; i < PROP_COUNT; i++) props.push(nextFloat(rng))
  const detail = hashSeed(seed >>> 0, FAMILY_SALT[kind], makerSalt, 0x64657461)
  const half = Math.ceil(slots.length / 2)
  const code = `${slots.slice(0, half).join('')}-${slots.slice(half).join('')}`
  return { family: familyId(kind, maker), slots, props, detail, code }
}

/** The parts standing at `stage` (0 = nothing yet, 5 = complete). */
export function buildMegaproject(kind: MegaKind, maker: MegaMaker, seed: number, site: SiteContext, stage: number): MegaPart[] {
  const f = requireGrammar(kind, maker)
  const k = Math.max(0, Math.min(MEGA_STAGES, Math.floor(stage)))
  if (k === 0) return []
  const g = formGenome(kind, maker, seed, site)
  return f.build(g, site, k).filter((p) => p.step <= k)
}

/** Number of slots in which two genomes differ. */
export function slotDistance(a: FormGenome, b: FormGenome): number {
  let n = 0
  for (let i = 0; i < a.slots.length; i++) if (a.slots[i] !== b.slots[i]) n++
  return n
}
